import { useState } from 'react';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Save, 
  Mail, 
  Bell, 
  AlertTriangle, 
  Brain,
  Settings,
  FileText,
  Sliders,
  RotateCcw,
} from 'lucide-react';
import { useBEStudy } from '@/hooks/useBEStudy';
import { AI_MODELS, SUPPORTED_FILE_FORMATS } from '@/constants/beStudyMockData';

/**
 * AI Configuration Section
 */
function AIConfigSection({ settings, onChange }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          AI Configuration
        </CardTitle>
        <CardDescription>
          Configure AI model and processing settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* AI Model Selection */}
        <div className="space-y-2">
          <Label>Default AI Model</Label>
          <Select
            value={settings.aiModel}
            onValueChange={(value) => onChange({ ...settings, aiModel: value })}
          >
            <SelectTrigger data-testid="ai-model-select">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {AI_MODELS.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  <div className="flex items-center gap-2">
                    <span>{model.name}</span>
                    <span className="text-xs text-muted-foreground">({model.provider})</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Model used for content generation and analysis
          </p>
        </div>

        {/* Enable AI Assist */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Enable AI Assist</Label>
            <p className="text-xs text-muted-foreground">
              Allow AI to assist with document generation
            </p>
          </div>
          <Switch
            checked={settings.enableAIAssist}
            onCheckedChange={(checked) => onChange({ ...settings, enableAIAssist: checked })}
            data-testid="enable-ai-assist-switch"
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Document Processing Section
 */
function DocumentProcessingSection({ settings, onChange }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sliders className="h-5 w-5" />
          Document Processing
        </CardTitle>
        <CardDescription>
          Configure confidence thresholds and processing rules
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Confidence Threshold */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Confidence Threshold</Label>
            <span className="text-sm font-medium">{settings.confidenceThreshold}%</span>
          </div>
          <Slider
            value={[settings.confidenceThreshold]}
            onValueChange={([value]) => onChange({ ...settings, confidenceThreshold: value })}
            min={0}
            max={100}
            step={5}
            data-testid="confidence-threshold-slider"
          />
          <p className="text-xs text-muted-foreground">
            Minimum confidence score required for AI-generated content to be accepted automatically
          </p>
        </div>

        {/* Auto-save Interval */}
        <div className="space-y-2">
          <Label htmlFor="autosave-interval">Auto-save Interval (seconds)</Label>
          <Input
            id="autosave-interval"
            type="number"
            value={settings.autoSaveInterval}
            onChange={(e) => onChange({ ...settings, autoSaveInterval: parseInt(e.target.value) || 30 })}
            min={10}
            max={300}
            data-testid="autosave-interval-input"
          />
          <p className="text-xs text-muted-foreground">
            How often documents are automatically saved
          </p>
        </div>

        {/* Version History */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Enable Version History</Label>
            <p className="text-xs text-muted-foreground">
              Track changes and maintain document versions
            </p>
          </div>
          <Switch
            checked={settings.enableVersionHistory}
            onCheckedChange={(checked) => onChange({ ...settings, enableVersionHistory: checked })}
            data-testid="enable-version-history-switch"
          />
        </div>

        {settings.enableVersionHistory && (
          <div className="space-y-2 pl-4 border-l-2 border-muted">
            <Label htmlFor="max-versions">Maximum Versions to Keep</Label>
            <Input
              id="max-versions"
              type="number"
              value={settings.maxVersionsToKeep}
              onChange={(e) => onChange({ ...settings, maxVersionsToKeep: parseInt(e.target.value) || 10 })}
              min={1}
              max={50}
              data-testid="max-versions-input"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * File Settings Section
 */
function FileSettingsSection({ settings, onChange }) {
  const handleFormatToggle = (formatId, checked) => {
    const newFormats = checked
      ? [...settings.supportedFormats, formatId]
      : settings.supportedFormats.filter(f => f !== formatId);
    onChange({ ...settings, supportedFormats: newFormats });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          File Settings
        </CardTitle>
        <CardDescription>
          Configure file upload limits and supported formats
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Max File Size */}
        <div className="space-y-2">
          <Label htmlFor="max-file-size">Maximum File Size (MB)</Label>
          <Input
            id="max-file-size"
            type="number"
            value={settings.maxFileSize}
            onChange={(e) => onChange({ ...settings, maxFileSize: parseInt(e.target.value) || 50 })}
            min={1}
            max={200}
            data-testid="max-file-size-input"
          />
          <p className="text-xs text-muted-foreground">
            Maximum allowed file size for uploads
          </p>
        </div>

        {/* Supported Formats */}
        <div className="space-y-3">
          <Label>Supported File Formats</Label>
          <div className="grid grid-cols-2 gap-3">
            {SUPPORTED_FILE_FORMATS.map((format) => (
              <div key={format.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`format-${format.id}`}
                  checked={settings.supportedFormats.includes(format.id)}
                  onCheckedChange={(checked) => handleFormatToggle(format.id, checked)}
                  data-testid={`format-${format.id}-checkbox`}
                />
                <label
                  htmlFor={`format-${format.id}`}
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {format.name} ({format.extension})
                </label>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Session Settings Section
 */
function SessionSettingsSection({ settings, onChange }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Session Settings
        </CardTitle>
        <CardDescription>
          Configure user session and timeout settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Session Timeout */}
        <div className="space-y-2">
          <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
          <Input
            id="session-timeout"
            type="number"
            value={settings.sessionTimeout}
            onChange={(e) => onChange({ ...settings, sessionTimeout: parseInt(e.target.value) || 60 })}
            min={5}
            max={480}
            data-testid="session-timeout-input"
          />
          <p className="text-xs text-muted-foreground">
            Duration of inactivity before automatic logout
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ConfigPage
 * Configuration management page with BE Study settings
 */
export const ConfigPage = () => {
  const { settings, updateSettings, resetSettings } = useBEStudy();
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleSettingsChange = (newSettings) => {
    setLocalSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = () => {
    setSaving(true);
    const result = updateSettings(localSettings);
    
    if (result.success) {
      toast.success('Configuration saved successfully');
      setHasChanges(false);
    } else {
      toast.error(result.error || 'Failed to save configuration');
    }
    setSaving(false);
  };

  const handleReset = () => {
    resetSettings();
    setLocalSettings(settings);
    setHasChanges(false);
    toast.info('Configuration reset to defaults');
  };

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="config-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Configuration</h1>
            <p className="text-muted-foreground">
              Manage system settings and preferences
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
              data-testid="reset-config-button"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              data-testid="save-config-button"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Configuration Tabs */}
        <Tabs defaultValue="ai" className="space-y-6" data-testid="config-tabs">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="ai" data-testid="ai-tab">
              <Brain className="h-4 w-4 mr-2" />
              AI Settings
            </TabsTrigger>
            <TabsTrigger value="processing" data-testid="processing-tab">
              <Sliders className="h-4 w-4 mr-2" />
              Processing
            </TabsTrigger>
            <TabsTrigger value="files" data-testid="files-tab">
              <FileText className="h-4 w-4 mr-2" />
              Files
            </TabsTrigger>
            <TabsTrigger value="session" data-testid="session-tab">
              <Settings className="h-4 w-4 mr-2" />
              Session
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai" className="space-y-4">
            <AIConfigSection 
              settings={localSettings} 
              onChange={handleSettingsChange} 
            />
          </TabsContent>

          <TabsContent value="processing" className="space-y-4">
            <DocumentProcessingSection 
              settings={localSettings} 
              onChange={handleSettingsChange} 
            />
          </TabsContent>

          <TabsContent value="files" className="space-y-4">
            <FileSettingsSection 
              settings={localSettings} 
              onChange={handleSettingsChange} 
            />
          </TabsContent>

          <TabsContent value="session" className="space-y-4">
            <SessionSettingsSection 
              settings={localSettings} 
              onChange={handleSettingsChange} 
            />
          </TabsContent>
        </Tabs>

        {/* Unsaved Changes Warning */}
        {hasChanges && (
          <div className="fixed bottom-6 right-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 shadow-lg z-50">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              You have unsaved changes. Don't forget to save!
            </p>
          </div>
        )}
      </div>
    </BaseLayout>
  );
};

export default ConfigPage;
